import { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard = ({ children }: AuthGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { token } = useSelector((state: RootState) => state.user);
  const sessionCountry = useSelector((state: any) => state.session.country);
  const isRehydrated = useSelector((state: any) => state._persist?.rehydrated);
  const isInitialMount = useRef(true);

  // Get country prefix from params if present
  const countryPrefix = params.country ? `/${params.country}` : '';

  useEffect(() => {
    if (!isRehydrated) return; // Wait for redux-persist

    const isLoginPage = location.pathname === '/login' || location.pathname.match(/^\/[a-z]{2,}\/login$/);
    const isOtpPage = location.pathname === '/otp' || location.pathname.match(/^\/[a-z]{2,}\/otp$/);
    const isEnterNamePage = location.pathname === '/enter-name' || location.pathname.match(/^\/[a-z]{2,}\/enter-name$/);

    if (token) {
      // Only redirect if session country matches URL country
      if (isLoginPage) {
        if (
          params.country &&
          sessionCountry &&
          params.country.toLowerCase() === sessionCountry.toLowerCase()
        ) {
          navigate(`/${params.country}/my-predictions`, { replace: true });
        }
        // Otherwise, do not redirect (CountryGuard or Login will show NotFound)
        return;
      }
    } else {
      // If user is not authenticated and tries to access protected pages, redirect to login
      if (!isLoginPage && !isOtpPage && !isEnterNamePage) {
        navigate(`${countryPrefix}/login`, { replace: true });
      }
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [token, location.pathname, navigate, location.state, countryPrefix, isRehydrated, params.country, sessionCountry]);

  if (!isRehydrated) return null;

  return <>{children}</>;
};